import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Search, DollarSign, TrendingUp, AlertTriangle, Package } from 'lucide-react';
import { WarrantyClaim } from '../types/analytics.types';
import { getWarrantyStatusColor } from '../utils/colorHelpers';

const WarrantyTracking: React.FC = () => {
    const [claims, setClaims] = useState<WarrantyClaim[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('All');
    const [showForecast, setShowForecast] = useState(false);

    useEffect(() => {
        setClaims([
            {
                id: 'CLM-001',
                claimNumber: 'CLM-2026-001',
                productName: 'Hydraulic Pump X200',
                customerName: 'Acme Corp',
                failureDate: new Date('2025-12-15'),
                ageAtFailure: 14,
                inWarranty: true,
                totalCost: 1250,
                status: 'Submitted',
                failureDescription: 'Leaking seal',
                createdByName: 'Field Tech',
                createdBy: 'user-1',
                saleDate: new Date('2024-10-01'),
                claimDate: new Date('2025-12-16'),
                warrantyPeriod: 24,
                failureMode: 'Seal Leak',
                repairCost: 500,
                replacementCost: 0,
                laborCost: 750,
                shippingCost: 0,
                resolutionDate: undefined,
                resolution: 'Pending',
                organizationId: 'org-1',
                createdAt: new Date(),
                updatedAt: new Date(),

                serialNumber: 'SN-001'
            },
            {
                id: 'CLM-002',
                claimNumber: 'CLM-2026-002',
                productName: 'Control Unit CU-100',
                customerName: 'Global Ind',
                failureDate: new Date('2026-01-10'),
                ageAtFailure: 2,
                inWarranty: true,
                totalCost: 450,
                status: 'Approved',
                failureDescription: 'Screen malfunction',
                createdByName: 'Customer',
                createdBy: 'user-2',
                saleDate: new Date('2025-11-01'),
                claimDate: new Date('2026-01-11'),
                warrantyPeriod: 12,
                failureMode: 'Screen',
                repairCost: 350,
                replacementCost: 0,
                laborCost: 100,
                shippingCost: 0,
                resolutionDate: new Date('2026-01-12'),
                resolution: 'Repaired',
                organizationId: 'org-1',
                createdAt: new Date(),
                updatedAt: new Date(),
                serialNumber: 'SN-002'
            }
        ]);
    }, []);

    // Calculate metrics
    const totalClaims = claims.length;
    const activeClaims = claims.filter(c => !['Resolved', 'Closed', 'Denied'].includes(c.status)).length;
    const totalCost = claims.reduce((sum, c) => sum + c.totalCost, 0);
    const averageCost = totalClaims > 0 ? totalCost / totalClaims : 0;

    // Filtered claims
    const filteredClaims = claims.filter(claim => {
        const matchesSearch =
            claim.claimNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            claim.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            claim.customerName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || claim.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Sample forecast data (normally calculated from historical data)
    const forecastMonths = 6;
    const monthlyForecast = Array.from({ length: forecastMonths }, (_, i) => ({
        month: new Date(Date.now() + i * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        predictedClaims: Math.floor(10 + Math.random() * 5),
        predictedCost: 5000 + Math.random() * 3000,
    }));

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Analytics
                </Link>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Warranty Tracking & Forecasting</h1>
                        <p className="text-gray-600 mt-1">Monitor warranty claims and predict future costs</p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowForecast(!showForecast)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <TrendingUp className="w-4 h-4 inline mr-2" />
                            {showForecast ? 'Hide' : 'Show'} Forecast
                        </button>
                        <Link
                            to="/warranty/new"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            New Claim
                        </Link>
                    </div>
                </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Total Claims</span>
                        <Package className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{totalClaims}</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Active Claims</span>
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="text-2xl font-bold text-yellow-600">{activeClaims}</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Total Cost</span>
                        <DollarSign className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="text-2xl font-bold text-red-600">${totalCost.toLocaleString()}</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Avg Cost</span>
                        <DollarSign className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="text-2xl font-bold text-blue-600">${averageCost.toLocaleString()}</div>
                </div>
            </div>

            {/* Forecast Section */}
            {showForecast && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        6-Month Forecast
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <div className="text-sm text-blue-600 mb-1">Predicted Total Claims</div>
                            <div className="text-3xl font-bold text-blue-900">
                                {monthlyForecast.reduce((sum, m) => sum + m.predictedClaims, 0)}
                            </div>
                            <div className="text-xs text-blue-700 mt-1">Next 6 months</div>
                        </div>

                        <div className="p-4 bg-red-50 rounded-lg">
                            <div className="text-sm text-red-600 mb-1">Predicted Total Cost</div>
                            <div className="text-3xl font-bold text-red-900">
                                ${monthlyForecast.reduce((sum, m) => sum + m.predictedCost, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                            <div className="text-xs text-red-700 mt-1">Next 6 months</div>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg">
                            <div className="text-sm text-green-600 mb-1">Trend</div>
                            <div className="text-3xl font-bold text-green-900">Stable</div>
                            <div className="text-xs text-green-700 mt-1">Based on historical data</div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Predicted Claims</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Predicted Cost</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {monthlyForecast.map((forecast, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900">{forecast.month}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{forecast.predictedClaims}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            ${forecast.predictedCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                                                90%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Search and Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by claim#, product, or customer..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Submitted">Submitted</option>
                            <option value="Under Review">Under Review</option>
                            <option value="Approved">Approved</option>
                            <option value="Denied">Denied</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Claims Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {filteredClaims.length > 0 ? (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claim #</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failure Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age (months)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredClaims.map((claim) => (
                                <tr key={claim.id} className="hover:bg-gray-50 cursor-pointer">
                                    <td className="px-4 py-3">
                                        <span className="font-mono text-sm font-medium text-blue-600">
                                            {claim.claimNumber}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{claim.productName}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{claim.customerName}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {claim.failureDate.toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        {claim.ageAtFailure}
                                        {claim.inWarranty && (
                                            <span className="ml-2 text-xs text-green-600">(In Warranty)</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        ${claim.totalCost.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getWarrantyStatusColor(claim.status)}`}>
                                            {claim.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-12 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">
                            {searchTerm || statusFilter !== 'All'
                                ? 'No warranty claims match your search criteria'
                                : 'No warranty claims yet'}
                        </p>
                        <Link
                            to="/warranty/new"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create First Claim
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WarrantyTracking;
